import FetchClients, { ClientOutput } from "../client";
import {
  useMutation,
  useQuery as useReactQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { CamelCasedProperties } from "type-fest";
import {
  recursiveCamelCaseCipher,
  recursiveSnakeCaseCipher,
} from "@src/global_functions/postgrestApi";
import certificationDocuments from "./certificationDocuments";
import {
  CheckFeatureAccessHeader,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { useSite } from "@src/context/SitesContext";
import React from "react";

const CERTIFICATIONS_QUERY_KEY = "certifications";
const keys = {
  all: [CERTIFICATIONS_QUERY_KEY],
  account: (accountId: number) => [
    CERTIFICATIONS_QUERY_KEY,
    "account",
    accountId,
  ],
  site: (siteId: number) => [CERTIFICATIONS_QUERY_KEY, "site", siteId],
};

function useInvalidateQueries() {
  const queryClient = useQueryClient();
  const invalidate = React.useCallback(
    ({ siteId, accountId }: { siteId?: number; accountId?: number }) =>
      Promise.all([
        ...(typeof siteId === "number"
          ? [queryClient.invalidateQueries({ queryKey: keys.site(siteId) })]
          : []),
        ...(typeof accountId === "number"
          ? [
              queryClient.invalidateQueries({
                queryKey: keys.account(accountId),
              }),
            ]
          : []),
      ]),
    [queryClient]
  );
  return invalidate;
}

const certifications = {
  useQuery,
  useQueryAccount,
  mutations: {
    usePatch,
    usePost,
    useDelete,
  },
  documents: certificationDocuments,
};

export type Certification = CamelCasedProperties<
  ClientOutput["site"]["Certification"]
>;
type CertificationRequest = CamelCasedProperties<
  ClientOutput["site"]["CertificationRequest"]
>;
export type CertificationStatus = ClientOutput["site"]["CertificationStatus"];
export type CertificationType = ClientOutput["site"]["CertificationType"];

const getCertificationsForSite = (siteId: number): Promise<Certification[]> => {
  return FetchClients.site
    .GET("/{site_id}/certifications", {
      params: {
        path: {
          site_id: siteId,
        },
      },
    })
    .then((res) => res.data && recursiveCamelCaseCipher(res.data));
};

function useQuery(siteId: number) {
  return useReactQuery({
    queryKey: keys.site(siteId),
    queryFn: () => getCertificationsForSite(siteId),
  });
}

// endpoint requires {feature: "Submissions", access: "read"}
const getCertificationsForAccount = (
  account_id: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<Certification[]> => {
  return FetchClients.certifications
    .GET("/", {
      params: {
        query: {
          account_id,
        },
      },
      headers: checkFeatureAccessHeader,
    })
    .then((res) => res.data && recursiveCamelCaseCipher(res.data));
};

function useQueryAccount(accountId: number) {
  return useQueryWithAccessCheckLog(
    ["Submissions", "read"],
    (checkFeatureAccessHeader) =>
      getCertificationsForAccount(accountId, checkFeatureAccessHeader),
    {
      queryKey: keys.account(accountId),
    }
  );
}

async function updateCertitication(
  siteId: number,
  certification: Certification
) {
  const response = await FetchClients.site.PATCH(
    "/{site_id}/certifications/{certification_id}",
    {
      params: { path: { site_id: siteId, certification_id: certification.id } },
      body: recursiveSnakeCaseCipher(certification),
    }
  );
  if (!response.data) {
    throw new Error("Failed to updated certification");
  }
  return response;
}

function usePatch(siteId: number) {
  const accountId = useSite(siteId)?.accountId;
  const invalidateQueries = useInvalidateQueries();
  return useMutation({
    mutationFn: (certification: Certification) =>
      updateCertitication(siteId, certification),
    onSettled: () => invalidateQueries({ siteId, accountId }),
  });
}

async function createCertitication(
  siteId: number,
  certification: CertificationRequest
) {
  const response = await FetchClients.site.POST("/{site_id}/certifications", {
    params: { path: { site_id: siteId } },
    body: recursiveSnakeCaseCipher(certification),
  });
  if (!response.data) {
    throw new Error("Failed to create certification");
  }
  return response.data;
}

function usePost(siteId: number) {
  const accountId = useSite(siteId)?.accountId;
  const invalidateQueries = useInvalidateQueries();
  return useMutation({
    mutationFn: (certification: Certification) =>
      createCertitication(siteId, certification),
    onSettled: () => invalidateQueries({ siteId, accountId }),
  });
}

function deleteCertitication(siteId: number, certificationId: number) {
  return FetchClients.site.DELETE(
    "/{site_id}/certifications/{certification_id}",
    {
      params: { path: { site_id: siteId, certification_id: certificationId } },
    }
  );
}

function useDelete(siteId: number) {
  const accountId = useSite(siteId)?.accountId;
  const invalidateQueries = useInvalidateQueries();
  return useMutation({
    mutationFn: (certificationId: number) =>
      deleteCertitication(siteId, certificationId),
    onSettled: () => invalidateQueries({ siteId, accountId }),
  });
}

export default certifications;
